// src/components/Credentialing.js
import React, { useState } from 'react';
import './Credentialing.css';

const Credentialing = () => {
  const [formData, setFormData] = useState({
    service: '',
    size: '',
    fullName: '',
    phone: '',
    email: '',
    website: '',
    reachVia: '',
    preferredTime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const serviceOptions = [
    'Credentialing Services',
    'Medical Billing',
    'Medical Coding',
    'Denial Management',
    'AR Follow-up',
    'Revenue Cycle Management'
  ];

  const sizeOptions = ['Solo Practitioner', 'Small Group', 'Multi-Provider Clinic', 'Large Organization', 'Multi-State Practice'];
  const reachViaOptions = ['Email', 'Phone', 'Both'];
  const timeOptions = ['Morning (9AM - 12PM)', 'Afternoon (12PM - 4PM)', 'Evening (4PM - 7PM)', 'Anytime'];

  const credentialingServices = [
    { title: 'Initial Enrollment Services', desc: 'We handle complex enrollment with all government programs, MCOs/IPAs/ACOs, and commercial payers ensuring your practice is properly set up for claims submission.' },
    { title: 'Demographics Update', desc: 'Our team ensures all provider and practice demographic information is current with payers, reducing claim rejections and delays.' },
    { title: 'Re-Credentialing Services', desc: 'We manage the re-credentialing process for existing providers and groups, keeping your contracts current and compliant with payer requirements.' },
    { title: 'Group & Individual Provider Enrollment', desc: 'Whether a solo practitioner or a multi-provider group, we manage all aspects of provider enrollment and credentialing services.' },
    { title: 'Fee Schedule Negotiation', desc: 'We help our practices negotiate their current fee schedule with the insurance companies for better reimbursement.' }
  ];

  const practiceTypes = [
    'Solo Practitioners', 'Small Group Practices', 'Multi-Provider Clinics',
    'Large Healthcare Organizations', 'Multi-State Practices', 'Multi-Location Practices',
    'Single-Specialty Practices', 'Multi-Specialty Practices'
  ];

  const processSteps = [
    'Application completion - complete and accurate',
    'Review of application - ensure complete and accurate',
    'Track application status and follow up with payer processing',
    'Use means to expedite the process if possible',
    'Manage multi-payer, multi-source, and multi-state credentialing',
    'Ensure compliance with re-licensed billing solutions and payer registration'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly.' });
        setFormData({ service: '', size: '', fullName: '', phone: '', email: '', website: '', reachVia: '', preferredTime: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <main className="credentialing-page">
      {/* Hero Section */}
    {/* Hero Section */}
<section className="cred-hero">
  <div className="container">
    <div className="cred-hero-content">
      <h1 className="cred-hero-title">
        Expert Medical Credentialing Services <span className="highlight">for Healthcare Providers</span>
      </h1>
      <p className="cred-hero-description">
        Provider Care Billing LLC simplifies the complex process of medical credentialing services, ensuring fast, 
        accurate, and fully compliant provider enrollment and credentialing service for healthcare organizations.
      </p>
      <div className="cred-hero-badges">
        <div className="hero-badge">Premium Medical Credentialing Services Starting at 2.49%</div>
        <div className="hero-badge">90.71% Clean Claims Rate for Faster Payments</div>
      </div>
    </div>
  </div>
</section>

      {/* Introduction Section */}
      <section className="cred-intro">
        <div className="container">
          <div className="cred-intro-content">
            <h2 className="cred-intro-title">Comprehensive Credentialing and Enrollment Services for Healthcare Providers</h2>
            <p className="cred-intro-text">
              Our credentialing and enrollment services help healthcare providers efficiently enroll with all government and 
              commercial payers, maintain active credentials, and stay compliant. Proper provider credentialing services 
              ensure timely reimbursement, minimize claim denials, and support seamless revenue cycle management (RCM).
            </p>
            <p className="cred-intro-text">
              Our experienced team manages the end-to-end provider enrollment services and physician credentialing services, 
              allowing you to focus on delivering quality patient care.
            </p>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="cred-consultation">
        <div className="container">
          <div className="cred-form-wrapper">
            <h2 className="cred-form-title">Get a free Consultation</h2>
            {submitStatus.message && <div className={`status-message ${submitStatus.type}`}>{submitStatus.message}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <select name="service" value={formData.service} onChange={handleChange} required>
                    <option value="">Select Service</option>
                    {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="size" value={formData.size} onChange={handleChange} required>
                    <option value="">Select Size</option>
                    {sizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required /></div>
                <div className="form-group"><input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required /></div>
                <div className="form-group"><input type="url" name="website" placeholder="Website/App" value={formData.website} onChange={handleChange} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <select name="reachVia" value={formData.reachVia} onChange={handleChange} required>
                    <option value="">Search Your VIA</option>
                    {reachViaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} required>
                    <option value="">Preferred Time</option>
                    {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="cred-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Get Started'}</button>
            </form>
          </div>
        </div>
      </section>

      {/* Credentialing First Step Section */}
      <section className="cred-first-step">
        <div className="container">
          <div className="first-step-content">
            <div className="first-step-text">
              <h2 className="first-step-title">Credentialing: The First Step Toward a Successful Medical Practice Startup & Growth</h2>
              <p className="first-step-description">
                Medical credentialing services form a critical foundation for launching and growing a medical practice. 
                Credentialing allows healthcare providers to enroll with government and commercial insurance payers, enabling 
                billing for services and timely reimbursement. Without proper insurance credentialing services, even established 
                practices may face claim denial, payment delays, and lost revenue.
              </p>
              <p className="first-step-description">
                Medical credentialing verifies a provider's qualifications, license, and compliance with payer requirements. 
                Completing this process early helps new practices start billing sooner, while ongoing credentialing and 
                re-credentialing support expansion, new provider onboarding, and multi-location growth.
              </p>
              <p className="first-step-description">
                By prioritizing medical credentialing services and partnering with an experienced medical credentialing company, 
                practices can avoid costly delays, maintain compliance, and build a strong revenue cycle from day one creating 
                a solid foundation for long-term success.
              </p>
            </div>
           <div className="first-step-image">
  <img 
    src={require('../assets/steps.png')} 
    alt="Medical Credentialing Process"
    className="first-step-img"
  />
</div>
          </div>
        </div>
      </section>

      {/* Our Expedited Services Section */}
      <section className="cred-services">
        <div className="container">
          <div className="cred-services-header">
            <h2 className="section-title-dark">Our Expedited Medical Credentialing Services</h2>
            <p className="section-subtitle-dark">
              Providers Care Billing LLC understands the complexities of medical credentialing can be overwhelming. 
              That's why we've simplified the process for you.
            </p>
          </div>
          <div className="cred-services-grid">
            {credentialingServices.map((service, index) => (
              <div className="cred-service-card" key={index}>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Insurance Credentialing Section */}
      <section className="cred-insurance">
        <div className="container">
          <div className="insurance-content">
            <h2 className="insurance-title">Comprehensive Insurance Credentialing for All Specialties & Practice Types</h2>
            <p className="insurance-text">
              Providers Care Billing LLC provides comprehensive insurance credentialing services for all specialties and practice 
              types. From solo practitioners to large multi-specialty clinics, our expert team handles provider enrollment, 
              re-credentialing, demographic updates, and multi-payer coordination. We ensure secure, compliant, and timely 
              credentialing so your practice can focus on patient care while maximizing reimbursements and minimizing revenue cycle delays.
            </p>
            <button className="insurance-demo-btn">Get a Free Demo →</button>
          </div>
         <div className="insurance-image">
  <img 
    src={require('../assets/comprehensive.jpg')} 
    alt="Insurance Credentialing Services"
    className="insurance-img"
  />
</div>
        </div>
      </section>

      {/* Tailored Services Section */}
      <section className="cred-tailored">
        <div className="container">
          <h2 className="tailored-title">Tailored Medical Credentialing Services for All Practices</h2>
          <p className="tailored-subtitle">
            Credentialing needs vary depending on the size and structure of a practice. Our services are tailored to meet 
            the unique needs of every practice/provider type and size.
          </p>
          <div className="practice-types-grid">
            {practiceTypes.map((type, index) => (
              <div className="practice-type-card" key={index}>
                <span className="practice-icon">✓</span>
                <span>{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="cred-benefits">
        <div className="container">
          <div className="benefits-wrapper">
            <h2 className="benefits-main-title">Benefits of Comprehensive Credentialing Services</h2>
            <p className="benefits-subtitle">
              Partnering with Provider Care Billing LLC for medical billing and credentialing services provides:
            </p>
            <div className="benefits-grid-cred">
              <div className="benefit-card-cred">
                <div className="benefit-icon-cred">⚡</div>
                <h3>Faster Payer Approvals</h3>
                <p>Associate and supervisor approvals, evidence preauthorization requests and speed up the current billings.</p>
              </div>
              <div className="benefit-card-cred">
                <div className="benefit-icon-cred">📉</div>
                <h3>Reduced Claim Denials</h3>
                <p>Faster resolution and lower claims review cycle time.</p>
              </div>
              <div className="benefit-card-cred">
                <div className="benefit-icon-cred">💰</div>
                <h3>Improved Cash Flow</h3>
                <p>Faster resolution and lower claims review cycle time.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline Section */}
      <section className="cred-timeline">
        <div className="container">
          <h2 className="timeline-title">How Long Does the Credentialing Process Take?</h2>
          <p className="timeline-subtitle">
            The credentialing process typically takes 30 to 120 days, but it's often much longer depending on multiple factors. 
            Some government programs, like Medicare and Medicaid, may require additional verification and longer review periods, 
            while commercial payers often process applications more quickly.
          </p>
          <div className="timeline-steps">
            {processSteps.map((step, index) => (
              <div className="timeline-step" key={index}>
                <span className="timeline-number">{index + 1}</span>
                <span className="timeline-text">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cred-cta">
        <div className="container">
          <div className="cta-content-cred">
            <h2>Ready to Get Credentialed?</h2>
            <p>Start your credentialing journey with Providers Care Billing LLC today and get paid faster.</p>
            <button className="cta-cred-btn">Schedule a Free Consultation →</button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Credentialing;